// English dictionary — the SINGLE SOURCE OF TRUTH for the app's copy.
//
// Every user-visible string (screen copy, onboarding, call-end messages,
// aria-labels, role names) lives here. `ru.ts` is typed against `Dictionary`
// (derived from this object), so a missing or extra Russian key is a compile
// error — EN can never drift ahead of RU silently.
//
// Values are either a plain string or a `PluralForms` object (used where a
// count drives the wording). `{name}`-style placeholders are filled by
// `translate()`; a plural entry additionally selects its form from `{count}`.

export interface PluralForms {
  one: string;
  few: string;
  many: string;
  other: string;
}
export type MessageValue = string | PluralForms;
export type MessageParams = Record<string, string | number>;

export const en = {
  // --- Common ---------------------------------------------------------------
  'common.loading': 'Loading…',
  'common.continue': 'Continue',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.save': 'Save',
  'common.accept': 'Accept',
  'common.decline': 'Decline',

  // --- Welcome (onboarding) -------------------------------------------------
  'welcome.tagline': 'Private 1:1 voice calls.',
  'welcome.create': 'Create account',
  'welcome.haveAccount': 'I already have an account',
  'welcome.restorePasskey': 'Restore with a passkey',

  // --- Display name (onboarding) --------------------------------------------
  'displayName.title': 'Pick a display name',
  'displayName.help': 'This is what friends see when you call. No email, no forms.',
  'displayName.label': 'Display name',

  // --- Pick avatar (onboarding, create path) --------------------------------
  'pickAvatar.title': 'Pick a profile picture',
  'pickAvatar.help':
    'A liquid-mercury picture, unique to your account. Your friends see the same one. You can change it later in Settings.',
  'pickAvatar.skip': 'Choose later',

  // --- Avatar picker (shared) -----------------------------------------------
  'avatarPicker.label': 'Profile picture options',
  'avatarPicker.option': 'Picture option {n}',
  'avatarPicker.showMore': 'Show more',

  // --- Recovery phrase (onboarding, create path) ----------------------------
  'showPhrase.title': 'Your recovery phrase',
  'showPhrase.intro1': 'These 24 words are the ',
  'showPhrase.only': 'only',
  'showPhrase.intro2':
    ' way to recover your account. If you lose this phrase and your device, your account is gone for good — there is no email or support desk that can bring it back. Write it down on paper and store it somewhere safe. Do not screenshot it or save it in a note app.',
  'showPhrase.wroteDown': 'I wrote my recovery phrase down',

  // --- Confirm phrase (onboarding, create path) -----------------------------
  'confirmPhrase.title': 'Confirm your recovery phrase',
  'confirmPhrase.help': 'Type the requested words from the phrase you just wrote down.',
  'confirmPhrase.wordLabel': 'Word #{n}',
  'confirmPhrase.error':
    "Those words don't match your recovery phrase. Check your written copy and try again.",

  // --- Restore phrase (onboarding, restore path) ----------------------------
  'restorePhrase.title': 'Restore your account',
  'restorePhrase.help':
    'Type or paste all 24 words of your recovery phrase in order. Spaces, line breaks, numbering and capitalization don’t matter. Restoring re-creates the exact same identity key, so your friends will see no key-change warning.',
  'restorePhrase.label': 'Recovery phrase',
  'restorePhrase.placeholder': 'apple banana cherry … (all 24 words, in order)',
  'restorePhrase.editWord': 'Edit word {n}',
  'restorePhrase.error':
    "That doesn't look like a valid 24-word recovery phrase. Check for typos and try again.",

  // --- Restore with a passkey (onboarding, WebAuthn PRF) --------------------
  'restorePasskey.title': 'Restore with a passkey',
  'restorePasskey.help':
    'Use a passkey you saved for your account — your device will offer it, nothing to type. It restores the exact same identity, so friends see no key-change warning. If it does not work, use your recovery phrase instead. A password alone can never restore an account — it is only an extra check on top of a passkey or phrase.',
  'restorePasskey.useButton': 'Use passkey',
  'restorePasskey.byId': 'Passkey not offered? Restore by account ID',
  'restorePasskey.label': 'Account ID',
  'restorePasskey.working': 'Restoring…',
  'restorePasskey.continue': 'Restore',
  'restorePasskey.failed':
    "Couldn't restore from a passkey for that account. Check the ID, or use your recovery phrase.",
  'restorePasskey.noCredential':
    'No passkey for your account was offered on this device. If you saved one elsewhere, try that device — or use your recovery phrase.',
  'restorePasskey.cancelled':
    'The passkey prompt was dismissed. Try again, or use your recovery phrase instead.',
  'restorePasskey.unsupported':
    "This browser can't use passkeys. Restore with your recovery phrase instead.",
  'restorePasskey.usePhrase': 'Use recovery phrase instead',

  // --- Restore password (onboarding, 2FA) -----------------------------------
  'restorePassword.title': 'Enter your account password',
  'restorePassword.help':
    'This account is protected by an account password — the second factor set in Settings. Enter it to finish restoring on this device.',
  'restorePassword.label': 'Password',
  'restorePassword.signingIn': 'Signing in…',
  'restorePassword.wrong': 'Wrong password. Try again.',
  'restorePassword.tooMany':
    'Too many attempts — the password was not checked. Wait a few minutes and try again.',
  'restorePassword.checkFailed':
    'Could not verify the password (connection problem). Nothing was signed in — try again.',

  // --- Microphone (onboarding, native) --------------------------------------
  'microphone.title': 'Allow the microphone',
  'microphone.help':
    "Merkury is a voice app — it needs the microphone to place and answer calls. Granting it now means an incoming call connects instantly instead of stalling on a permission prompt you can't see over the lock screen.",
  'microphone.denied':
    "The microphone is off, so calls won't have any audio. You can allow it any time in your system settings under Merkury.",
  'microphone.allow': 'Allow microphone',
  'microphone.skip': 'Skip for now',

  // --- Notifications (onboarding) -------------------------------------------
  'onbNotifications.title': 'Turn on notifications',
  'onbNotifications.help':
    "Friends' calls only ring if notifications are allowed. Without them, Merkury can't wake up to alert you while the app is in the background or closed.",
  'onbNotifications.denied':
    "Notifications are off, so incoming calls won't ring while the app is closed. You can allow them any time in your system settings under Merkury.",
  'onbNotifications.enable': 'Enable notifications',
  'onbNotifications.skip': 'Skip for now',

  // --- Install hint (Safari/iOS "add to Home Screen" path, JW9) --------------
  'installHint.ios.title': 'Add Merkury to your Home Screen',
  'installHint.ios.body':
    'On iPhone and iPad, Merkury can only ring for incoming calls after it is added to your Home Screen — in a browser tab it can’t wake up to alert you.',
  'installHint.ios.step1': 'Tap the Share button in the toolbar.',
  'installHint.ios.step2': 'Choose “Add to Home Screen”.',
  'installHint.ios.step3': 'Open Merkury from your Home Screen.',
  'installHint.denied.title': 'Notifications are blocked',
  'installHint.denied.body':
    'Incoming calls can’t ring while Merkury is closed until notifications are allowed. Turn them on for Merkury in your browser or system settings.',
  'installHint.dismiss': 'Dismiss',

  // --- Session ended (definitive remote revocation) ---------------------------
  'auth.sessionEnded.title': 'Session ended',
  'auth.sessionEnded.body':
    "This device's session ended — from another device, or because it needed a fresh login. Restore with your passkey or recovery phrase to keep using Merkury here.",
  'auth.sessionEnded.action': 'Restore access',

  // --- Settings (aggregator) ------------------------------------------------
  'settings.title': 'Settings',
  'settings.loading': 'Loading settings…',
  'settings.echoTest': 'Echo test',
  'settings.echoHelp':
    'Records ~{secs} s from your mic and plays it back — checks your mic, speaker and permissions without calling anyone.',
  'settings.recording': 'Recording… speak now',
  'settings.playingBack': 'Playing back…',
  'settings.audioLevel': 'Audio level',
  'settings.runAgain': 'Run again',
  'settings.startEcho': 'Start echo test',
  'settings.echoDone': 'Echo test finished — if you heard yourself, audio works.',
  'settings.echoFailed': 'Echo test failed: {error}',
  'settings.recoveryPhrase': 'Recovery phrase',
  'settings.recoveryWarn':
    'Anyone who sees these 24 words owns your account. Make sure nobody is watching your screen.',
  'settings.nobodyWatching': 'Nobody can see my screen',
  'settings.reveal': 'Reveal recovery phrase',
  'settings.hide': 'Hide',
  'settings.appVersion': 'App version',
  'settings.webClient': 'Merkury Web',
  'settings.version': 'ver. {version}',
  'settings.notConnectedTitle': 'Not connected yet',
  'settings.notConnectedBody':
    "This device hasn't registered with a server, so account settings aren't available yet. Registration retries automatically whenever a server is reachable.",
  'settings.groups': 'Settings groups',
  'settings.account': 'Account',
  'settings.security': 'Security',
  'settings.notifications': 'Notifications',
  'settings.calls': 'Calls',
  'settings.sessions': 'Sessions',
  'settings.language': 'Language',
  'settings.ringtone': 'Ringtone',
  'settings.ringtoneHelp': 'Tap a tone to hear it and set it as your incoming-call ring.',
  'settings.ringtonePlaying': 'Playing…',
  'ringtone.chime': 'Chime',
  'ringtone.arcade': 'Arcade',
  'ringtone.blip': 'Blip',
  'ringtone.pulse': 'Pulse',
  'ringtone.crystal': 'Crystal',
  'ringtone.coin': 'Coin',
  'ringtone.march': 'March',
  'ringtone.bounce': 'Bounce',
  'ringtone.laser': 'Laser',
  'ringtone.fanfare': 'Fanfare',
  'settings.android': 'Android settings',
  'settings.developer': 'Developer',
  'settings.aboutConnection': 'About this connection',
  'settings.endpointVersion': 'Endpoint list version:',
  'settings.closeDialog': 'Close settings',

  // --- App updates (Android self-update, docs/06 §4) -------------------------
  'updates.title': 'App updates',
  'updates.check': 'Check for update',
  'updates.install': 'Download and install',
  'updates.checking': 'Checking…',
  'updates.downloading': 'Downloading update…',
  'updates.upToDate': 'Merkury is up to date (checked {time}).',
  'updates.notChecked': 'Not checked yet.',
  'updates.available': 'A new version is ready to install.',
  'updates.popupTitle': 'Update available',
  'updates.later': 'Later',
  'updates.installPrompted':
    'Confirm the installation in the system prompt. Merkury restarts on the new version.',
  'updates.installPermission':
    'Allow “Install unknown apps” for Merkury in the screen that just opened, then tap install again.',
  'updates.errorOffline': 'Could not reach the server. Try again later.',
  'updates.errorRejected': 'Update rejected: the signature check failed.',
  'updates.errorDownload': 'Download failed on every mirror. Try again later.',

  // --- Settings chrome ------------------------------------------------------
  'chrome.backToSettings': 'Back to settings',

  // --- Language settings ----------------------------------------------------
  'language.title': 'Language',
  'language.system': 'System default',
  'language.en': 'English',
  'language.ru': 'Русский',

  // --- Account settings -----------------------------------------------------
  'account.title': 'Account',
  'account.displayName': 'Display name',
  'account.saved': 'Saved',
  'account.profilePicture': 'Profile picture',
  'account.profilePictureHelp':
    'A liquid-mercury picture generated from your account. Pick the one you like — your friends see the same one.',
  'account.avatarCurrent': 'Your current picture',
  'account.noPictureYet': 'No picture chosen yet. Pick one below.',
  'account.avatarOption': 'Picture option {n}',
  'account.showMore': 'Show more',
  'account.couldNotUpdateAvatar': 'Could not update your picture. Try again.',
  'account.safetyNumbers': 'Safety numbers',
  'account.safetyHelp':
    'Read this aloud in a call — if it matches on both phones, the call is not intercepted.',
  'account.contact': 'Contact',
  'account.chooseContact': 'Choose a contact…',
  'account.unavailableKey': 'Unavailable — key not known yet.',
  'account.password': 'Account password',
  'account.useAtLeast8': 'Use at least 8 characters.',
  'account.passwordsDontMatch': "Passwords don't match.",
  'account.passwordChanged': 'Password changed.',
  'account.passwordSet': 'Password set.',
  'account.couldNotUpdatePassword': 'Could not update the password. Try again.',
  'account.passwordRemoved':
    'Password removed. A passkey or the recovery phrase alone can now restore the account on a new device.',
  'account.couldNotRemovePassword': 'Could not remove the password. Try again.',
  'account.passwordSetHelp':
    'A password is set. It works as a second factor: restoring on a new device needs a passkey or the recovery phrase AND this password. The password alone can never restore the account — but without it, neither can they.',
  'account.passwordOptionalHelp':
    'Optional second factor. With a password set, a stolen recovery phrase or passkey alone is not enough to take over the account — restoring on a new device requires the password too. The password by itself never restores anything.',
  'account.newPassword': 'New password',
  'account.passwordLabel': 'Password',
  'account.confirmPassword': 'Confirm password',
  'account.signOutOthers': 'Also sign out my other devices',
  'account.changePassword': 'Change password',
  'account.setPassword': 'Set password',
  'account.removePassword': 'Remove password',
  'account.blockedUsers': 'Blocked users',
  'account.nobodyBlocked': 'Nobody is blocked.',
  'account.unblock': 'Unblock',
  'account.unblockAria': 'Unblock {userId}',
  'account.deleteAccount': 'Delete account',
  'account.deleteHelp':
    'Permanently erases your account, friendships and call history. Friends will simply see you disappear. Your recovery phrase becomes useless. This cannot be undone.',
  'account.deleteOpen': 'Delete my account…',
  'account.typeIdConfirm': 'Type your ID ({userId}) to confirm',
  'account.yourPassword': 'Your account password',
  'account.deleting': 'Deleting…',
  'account.permanentlyDelete': 'Permanently delete',
  'account.deletionFailed': 'Deletion failed. Check the password and try again.',

  // --- Sign out -------------------------------------------------------------
  'account.signOut': 'Sign out',
  'account.signOutHelp':
    'Signs out on this device and removes the identity stored here. Your account is not deleted.',
  'account.signOutConfirm':
    "You'll need your passkey or recovery phrase to sign back in — plus your account password, if one is set. Without these you cannot get back into this account.",
  'account.signOutConfirmButton': 'Sign out',
  'account.signingOut': 'Signing out…',

  // --- Passkeys -------------------------------------------------------------
  'passkeys.title': 'Passkeys',
  'passkeys.help':
    'Passkeys sign this device into the server. A passkey that supports PRF can also restore your account on a new device; otherwise your recovery phrase is still required.',
  'passkeys.couldNotLoad': 'Could not load passkeys.',
  'passkeys.unsupported':
    "This device can't manage passkeys. Add one from the web app in a browser that supports them.",
  'passkeys.restorable': 'Added. This passkey can restore your account on a new device.',
  'passkeys.sessionOnly':
    "Added. This passkey signs in on this device only — it can't restore a new device, so keep your recovery phrase.",
  'passkeys.registered': 'Registered passkeys',
  'passkeys.none': 'No passkeys yet.',
  'passkeys.defaultLabel': 'Passkey',
  'passkeys.remove': 'Remove',
  'passkeys.couldNotAdd': 'Could not add a passkey. Please try again.',
  'passkeys.alreadyOnDevice': 'This device already has a Merkury passkey. Remove it first to add a new one.',
  'passkeys.expiredRetry': 'That took too long — the passkey request expired. Tap Add passkey again.',
  'passkeys.wrongOrigin':
    'This browser can only use passkeys on the main site. Open {domain} and add your passkey there.',
  'passkeys.couldNotRemove': 'Could not remove that passkey.',
  'passkeys.namePlaceholder': 'Passkey name (optional)',
  'passkeys.newNameAria': 'New passkey name',
  'passkeys.adding': 'Adding…',
  'passkeys.add': 'Add passkey',

  // --- Sessions -------------------------------------------------------------
  'sessions.title': 'Sessions',
  'sessions.kindAndroid': 'Android app',
  'sessions.kindWeb': 'Web',
  'sessions.active': 'Active sessions',
  'sessions.thisDevice': 'this device',
  'sessions.lastSeenLine': '{kind} · last seen {when}',
  'sessions.unknown': 'unknown',
  'sessions.terminate': 'Terminate',
  'sessions.terminateAria': 'Terminate session {name}',
  'sessions.terminateAll': 'Terminate all other sessions',

  // --- Notifications settings -----------------------------------------------
  'notifSettings.title': 'Notifications',
  'notifSettings.callNotifications': 'Call notifications',
  'notifSettings.body':
    "Merkury notifies you of incoming calls using your device's notification permission. Grant notifications to Merkury in your browser or system settings to be alerted when the app isn't open.",
  'notifSettings.web.enable': 'Enable notifications',
  'notifSettings.web.granted': 'Notifications are on — incoming calls will ring on this device.',
  'notifSettings.web.denied':
    'Notifications are blocked for Merkury. Turn them back on in your browser’s site settings for this page, then reload.',
  'notifSettings.web.unsupported': 'This browser does not support notifications.',
  'notifHealth.allOk': 'All notification checks passed — incoming calls will ring on this device.',
  'notifHealth.blocked.title': 'Notifications are turned off',
  'notifHealth.blocked.body':
    "Merkury can't show incoming calls because notifications are off. Without them a call only vibrates, with no way to answer. Turn notifications on so incoming calls ring.",
  'notifHealth.blocked.action': 'Open notification settings',
  'notifHealth.channel.title': 'Incoming-call alerts are silenced',
  'notifHealth.channel.body':
    'The "Incoming calls" notification category was disabled or demoted, so a call can\'t pop up or ring. Set it back to full alerting.',
  'notifHealth.channel.action': 'Open category settings',
  'notifHealth.dnd.title': "Calls won't ring in Do Not Disturb",
  'notifHealth.dnd.body':
    'The "Incoming calls" category is not allowed to override Do Not Disturb, so calls stay silent while it\'s on. Enable "Override Do Not Disturb" for this category.',
  'notifHealth.dnd.action': 'Open category settings',
  'notifHealth.fullScreen.title': 'Full-screen call alerts are off',
  'notifHealth.fullScreen.body':
    'Android is blocking Merkury from showing the ringing screen when the phone is locked (some phones reset this on every app update). Allow full-screen notifications — if the toggle already shows ON, switch it off and back on.',
  'notifHealth.fullScreen.action': 'Open settings',
  'notifHealth.battery.title': 'Battery optimization may delay calls',
  'notifHealth.battery.body':
    'Android may put Merkury to sleep in the background, delaying or dropping incoming-call pushes. Exempt Merkury from battery optimization for reliable ringing.',
  'notifHealth.battery.action': 'Disable battery optimization',

  // --- Ringtone settings ----------------------------------------------------
  'ringtone.title': 'Ringtone',
  'ringtone.help': 'The sound that plays for incoming calls.',
  'ringtone.current': 'Current:',
  'ringtone.default': 'Default',
  'ringtone.change': 'Change ringtone',

  // --- Android settings -----------------------------------------------------
  'android.title': 'Android',
  'android.stayReachable': 'Stay reachable',
  'android.stayReachableHelp':
    'Keeps a persistent connection for incoming calls without Google push — uses more battery and shows a permanent notification (Android requires it).',
  'android.batteryWarn': 'Battery optimization may delay calls.',
  'android.allowBattery': 'Allow unrestricted battery use',
  'android.pushTransport': 'Push transport',
  'android.fcm': 'Google (FCM) — default',
  'android.ntfy': 'ntfy (UnifiedPush) — for networks where Google is blocked',
  'android.ntfyHelp': 'ntfy needs a UnifiedPush distributor app installed (e.g. the ntfy app).',
  'android.noDistributor':
    'No UnifiedPush distributor found — staying on Google push until one is installed.',
  'android.ntfyAppTitle': 'Get the ntfy app',
  'android.ntfyAppHelp':
    "Merkury can receive calls even when it isn't running, if the ntfy app is installed. ntfy keeps one lightweight connection open in the background and wakes Merkury the moment a call arrives — without it, calls only ring while Merkury's own background service stays alive.",
  'android.ntfyAppTrust': "ntfy talks only to Merkury's own push server — no Google involved.",
  'android.ntfyAppGet': 'Get ntfy from Google Play',

  // --- Developer screen -----------------------------------------------------
  'developer.title': 'Developer',
  'developer.testing': 'Testing…',
  'developer.reachable': 'Server reachable · {ms} ms',
  'developer.httpError': 'Reached the server, but /healthz returned HTTP {status}.',
  'developer.corsBlocked':
    'Server is reachable but blocks cross-origin requests — start it with DEV_CORS_ORIGINS=*.',
  'developer.unreachable': 'Could not reach the server. Check the address, Wi-Fi, and any firewall.',
  'developer.timeout': 'No response within {secs} s.',
  'developer.invalid': 'Enter an origin like http://192.168.1.5:8080.',
  'developer.backendOverride': 'Backend override',
  'developer.overrideHelp1':
    'Point this app at a different backend (e.g. a laptop on your Wi-Fi). Enter an origin like ',
  'developer.overrideHelp2': '. Test only probes; nothing applies until Save & reload.',
  'developer.activeOverride': 'Active override:',
  'developer.placeholder': 'http://host:port',
  'developer.serverAddressAria': 'Backend server address',
  'developer.test': 'Test',
  'developer.saveReload': 'Save & reload',
  'developer.resetDefault': 'Reset to default',

  // --- Developer → Diagnostics (docs/10 §§1/4/5) -----------------------------
  'diag.title': 'Diagnostics',
  'diag.tabBrainLog': 'Brain log',
  'diag.tabAppLog': 'App log',
  'diag.tabSnapshot': 'Snapshot',
  'diag.logEmpty': 'No log lines yet.',
  'diag.copyPage': 'Copy this page',
  'diag.copyAll': 'Copy everything',
  'diag.copied': 'Copied to the clipboard.',
  'diag.copyFailed':
    'The browser would not hand over the clipboard. The text is below — select and copy it, or photograph it; that works too.',
  'diag.copyFallbackLabel': 'Diagnostics text to copy',
  'diag.autoSend': 'Send error reports automatically',
  'diag.autoSendHelp':
    'Anonymous reports about failed connections: error codes, public server names, your country and mobile carrier. Never names, contacts, message content, or your IP address.',
  'diag.autoSendOff':
    'Off. Nothing is sent, and nothing is offered to send — not on a schedule, and not when the app cannot reach its servers. You can still copy a report and send it yourself.',
  'diag.previewTitle': 'Preview report',
  'diag.previewHelp': 'The exact bytes that would be uploaded, shown before anything is sent.',
  'diag.previewAction': 'Show the report',
  'diag.previewLoading': 'Building the report…',
  'diag.previewEmpty':
    'No report right now: nothing has failed since the last one, so there is nothing to send.',
  'diag.previewEmptyWhy':
    'Diagnostics is running — {brain} brain log lines and {app} app log lines recorded this session. A report is cut when a connection fails; the logs above can be copied at any time.',
  'diag.previewFailed':
    'The report could not be built — diagnostics storage did not answer. Try again; copying the logs above works either way.',
  'diag.previewCopy': 'Copy the report',
  'diag.previewSend': 'Send this report now',
  'diag.previewSent': 'Queued. It goes out over whichever route still works.',
  'diag.previewSendFailed':
    'The report was not queued. It is still here — copy it and send it through a channel you trust.',
  'diag.disclosureTitle': 'Developer mode is on',
  'diag.disclosureBody':
    'Merkury will now send anonymous error logs so we can see where the app is being blocked and make it harder to block. Reports carry error codes, public server names, your country and mobile carrier, and your app version.',
  'diag.disclosureNever':
    'They never carry your name, your contacts, anything you said, or your IP address — and you can read a report in full before it goes.',
  'diag.disclosureWhere':
    'You can turn this off at any time: Settings → Developer → Diagnostics → “{toggle}”.',
  'diag.unavailable':
    'Diagnostics storage could not be opened, so the toggle below is not live. Copying still works.',
  'diag.disclosureOk': 'Got it',

  // --- Contacts -------------------------------------------------------------
  'contacts.title': 'Contacts',
  'contacts.offlineAria': 'Merkury is offline',
  'contacts.signalingDown': 'Signaling connection is down',
  'contacts.yourId': 'Your ID',
  'contacts.registering': 'registering…',
  'contacts.copied': 'Copied',
  'contacts.copy': 'Copy',
  'contacts.allContacts': 'All contacts',
  'contacts.noContacts': 'No contacts yet',
  'contacts.noContactsHelp':
    "Tap + to share your ID or scan a friend's code — calls only work between contacts.",
  'contacts.blocked': 'Blocked',
  'contacts.openAria': 'Open {name}',
  'contacts.callAria': 'Call {name}',
  'contacts.requests': 'Requests',
  'contacts.friendRequests': 'Friend requests',
  'contacts.addFriendAria': 'Add friend',
  'contacts.noRequests': 'No pending requests',
  'contacts.noRequestsHelp': 'When someone adds your ID, their request will land here.',
  'contacts.requestsBadge': {
    one: '{count} incoming request',
    few: '{count} incoming requests',
    many: '{count} incoming requests',
    other: '{count} incoming requests',
  },

  // --- Contact detail -------------------------------------------------------
  'contactDetail.back': 'Back to contacts',
  'contactDetail.callHistory': 'Call history',
  'contactDetail.history': 'History',
  'contactDetail.noCalls': 'No calls with this contact yet.',
  'contactDetail.call': 'Call',
  'contactDetail.removeFav': 'Remove from favorites',
  'contactDetail.addFav': 'Add to favorites',
  'contactDetail.unblock': 'Unblock',
  'contactDetail.block': 'Block user',
  'contactDetail.blockConfirm':
    'Block {name}? They will not be able to call you, and they are not told. Blocking also removes them from your contacts — if you unblock them later, you will need to add them again before you can call each other.',
  'contactDetail.blockConfirmButton': 'Yes, block',
  'contactDetail.couldNotBlock':
    'Could not block this contact — check your connection and try again.',
  'contactDetail.couldNotUnblock':
    'Could not unblock this contact — check your connection and try again.',
  'contactDetail.removeFriend': 'Remove friend',
  'contactDetail.removeFriendConfirm':
    "Remove {name} from your contacts? You'll need to add each other again to call.",
  'contactDetail.removeFriendConfirmButton': 'Yes, remove friend',
  'contactDetail.removingFriend': 'Removing…',
  'contactDetail.couldNotRemoveFriend':
    'Could not remove this contact — check your connection and try again.',
  'contactDetail.presenceApp': 'Online',
  'contactDetail.presenceReachable': 'Reachable',
  'contactDetail.presenceOffline': 'Offline',
  'contactDetail.nickname': 'Nickname',
  'contactDetail.aka': 'aka {name}',
  'contactDetail.nicknameClear': 'Clear',

  // --- Empty pane (desktop right-pane default) ------------------------------
  'emptyPane.brand': 'Merkury',
  'emptyPane.selectContact': 'Select a contact to view details or place a call',
  'emptyPane.yourId': 'Your ID',
  'emptyPane.showQr': 'Show QR',
  'emptyPane.hideQr': 'Hide QR',

  // --- Main (Favorites + Recents) -------------------------------------------
  'main.serverConnection': 'Server connection: {label}',
  'main.serverSignaling': 'Server signaling connection',
  'main.signalingDown': 'down',
  'main.signalingConnecting': 'connecting',
  'main.signalingConnected': 'connected',
  'main.signalingReconnecting': 'reconnecting',
  'main.trying': 'Trying to reach Merkury… (attempt {n})',
  'main.retryNow': 'Retry now',
  'main.favorites': 'Favorites',
  'main.recents': 'Recents',
  'main.noCalls': 'No calls yet',
  'main.noCallsHelp': 'Calls you make and receive will show up here.',
  'main.callBackAria': 'Call back {name}',
  'main.missedBadge': {
    one: '{count} missed call',
    few: '{count} missed calls',
    many: '{count} missed calls',
    other: '{count} missed calls',
  },

  // --- Sidebar (desktop shell) ----------------------------------------------
  'sidebar.searchPlaceholder': 'Search',
  'sidebar.searchAria': 'Search contacts and recents',
  'sidebar.noMatches': 'No matches',

  // --- Sync / dialer notices (shared) ---------------------------------------
  'sync.refreshError': 'Could not refresh from the server — showing the last known list.',
  'sync.refreshing': 'Refreshing…',
  'call.offlineHint': "{name} is offline — they'll see a missed call",
  'dialer.couldNotStart': 'Could not start the call — please try again.',

  // --- Call history formatting ----------------------------------------------
  'callFormat.noAnswer': 'no answer',
  'callFormat.missed': 'missed',
  'callFormat.declined': 'declined',
  'callFormat.failed': 'failed',

  // --- In-call screen -------------------------------------------------------
  'call.end.hangup': 'Call ended',
  'call.end.declined': 'Call declined',
  'call.end.busy': 'Busy',
  'call.end.no_answer': 'No answer',
  'call.end.missed': 'Missed call',
  'call.end.answered_elsewhere': 'Answered on another device',
  'call.end.connection_lost': 'Connection lost',
  'call.end.failed': 'Could not connect',
  'call.end.mic_denied': 'Microphone access needed — allow it for Merkury in system settings',
  'call.end.micBlocked':
    'Microphone is blocked for Merkury in this browser. Enable it in the site settings (tap the lock icon in the address bar), then try again.',
  'call.end.security': 'Call blocked: security check failed',
  'call.phase.dialing': 'Calling…',
  'call.phase.ringing_remote': 'Ringing…',
  'call.phase.connecting': 'Connecting…',
  'call.phase.reconnecting': 'Reconnecting…',
  'call.keyChanged': 'Safety key changed',
  'call.keyChangedBody':
    'The security key for this contact has changed. This can happen when they reinstall — or if someone is intercepting the call. Verify the safety number with them before trusting the new key.',
  'call.trustNewKey': 'Trust new key',
  'call.noActive': 'No active call.',
  'call.callBack': 'Call back',
  /** Fallback shown wherever a peer name is rendered but the friends cache
   *  has no match (not a friend, or the cache/list hasn't loaded yet) — the
   *  in-call/incoming screens, Recents, and contact detail all use this
   *  instead of ever falling back to the raw account UUID. */
  'call.unknownCaller': 'Unknown caller',
  'call.returnToCall': 'Return to call with {name}',
  'call.returnToCallWithTime': 'Return to call with {name} — {time}',
  'call.duration': 'Call duration',
  'call.safetyNumber': 'Safety number',
  'call.viaRelay': 'via relay',
  'call.connectionQuality': 'Connection quality',
  'call.controls': 'Call controls',
  'call.mute': 'Mute',
  'call.unmute': 'Unmute',
  'call.speakerOn': 'Speaker on',
  'call.speakerOff': 'Speaker off',
  'call.routeEarpiece': 'Audio: earpiece — tap to change',
  'call.routeSpeaker': 'Audio: speaker — tap to change',
  'call.routeBluetooth': 'Audio: Bluetooth — tap to change',
  'call.routeWired': 'Audio: wired headset — tap to change',
  'call.endCall': 'End call',
  // Short labels under the round desktop in-call controls (state shows in colour).
  'call.muteShort': 'Mute',
  'call.audioShort': 'Audio',
  'call.endShort': 'End',

  // --- Incoming call screen -------------------------------------------------
  'incoming.isCalling': 'is calling',
  'incoming.answerControls': 'Answer controls',
  /** Generic title for the provisional cold-open ring, before the invite has
   *  reached the engine and the caller is known. */
  'incoming.incomingCall': 'Incoming call',
  /** Provisional-ring status line while the invite is still arriving. */
  'incoming.connecting': 'Connecting…',

  // --- Add friend -----------------------------------------------------------
  'addFriend.cameraDenied':
    'Camera access was denied. Allow the camera for Merkury in your system settings, or paste the ID below.',
  'addFriend.noCamera': 'No usable camera found — paste the ID below instead.',
  'addFriend.cameraUnavailable': 'Camera unavailable. Paste the ID instead.',
  'addFriend.ownId': "That's your own ID.",
  'addFriend.requestSent': 'Friend request sent.',
  'addFriend.couldNotSend': 'Could not send the request. Check the ID and try again.',
  'addFriend.notAnId': "That doesn't look like a Merkury ID or add-link.",
  'addFriend.copied': 'Copied to clipboard.',
  'addFriend.backHome': 'Back to home',
  'addFriend.title': 'Add friend',
  'addFriend.myCode': 'My code',
  'addFriend.shareLink': 'Share my link',
  'addFriend.shareId': 'Share my ID',
  'addFriend.registering': 'Your ID is still being registered…',
  'addFriend.scanCode': 'Scan a code',
  'addFriend.stopScanning': 'Stop scanning',
  'addFriend.scanFriend': "Scan a friend's code",
  'addFriend.scanUnsupported':
    "Scanning isn't supported on this device — paste the ID below instead.",
  'addFriend.pasteId': 'Paste an ID',
  'addFriend.friendIdLabel': "Friend's ID or add-link",
  'addFriend.sendRequest': 'Send request',
  'addFriend.pendingRequests': 'Pending requests',
  'addFriend.pending': 'Pending',
  'addFriend.noPending': 'No pending requests.',
  'addFriend.incoming': 'incoming',
  'addFriend.outgoingPending': 'outgoing · pending',

  // --- QR code --------------------------------------------------------------
  'qr.error': 'Could not generate a QR code for this link.',
  'qr.aria': 'QR code for {value}',

  // --- Tab bar --------------------------------------------------------------
  'tabs.primary': 'Primary',
  'tabs.main': 'Main',
  'tabs.contacts': 'Contacts',
  'tabs.settings': 'Settings',

  // --- Push notifications (service worker) -----------------------------------
  'push.incomingTitle': 'Incoming call',
  'push.missedTitle': 'Missed call',
  'push.incomingBody': 'Someone is calling you on Merkury.',
  'push.missedBody': 'You missed a call on Merkury.',
  'push.accept': 'Accept',
  'push.decline': 'Decline',
  'push.endedTitle': 'Call ended',
  'push.endedBody': 'The call is no longer active.',
} satisfies Record<string, MessageValue>;

export type MessageKey = keyof typeof en;
export type Dictionary = {
  [K in MessageKey]: (typeof en)[K] extends string ? string : PluralForms;
};
